import type { ReactNode } from 'react';
import { DAYS_OF_WEEK, hasWorkingDay, toggleWorkingDay } from '@nest-recruit/shared';
import styles from './WorkingDayPicker.module.css';

/**
 * Seven toggles for the working week.
 *
 * A shared component rather than one local to ADMIN-10, because §L23's shift and
 * availability screens later need the same control and the alternative — a row of
 * checkboxes — reads as a form to fill in rather than a week to look at. Seven
 * things you can see at once should be seen at once.
 *
 * `role="group"` with real buttons rather than a checkbox each: the value is one
 * number, not seven booleans, and tabbing through seven checkboxes to set a working
 * week is exactly the kind of friction CLAUDE.md §4 exists to stop.
 */

interface WorkingDayPickerProps {
  readonly value: number;
  readonly onChange: (value: number) => void;
  readonly disabled?: boolean;
  readonly describedBy?: string;
  readonly id?: string;
}

export function WorkingDayPicker({
  value,
  onChange,
  disabled,
  describedBy,
  id,
}: WorkingDayPickerProps): ReactNode {
  return (
    <div
      className={styles.group}
      role="group"
      aria-label="Working days"
      aria-describedby={describedBy}
      id={id}
    >
      {DAYS_OF_WEEK.map((day) => {
        const isOn = hasWorkingDay(value, day.bit);
        return (
          <button
            key={day.bit}
            type="button"
            className={styles.day}
            aria-pressed={isOn}
            aria-label={day.label}
            disabled={disabled}
            onClick={() => onChange(toggleWorkingDay(value, day.bit))}
          >
            {day.short}
          </button>
        );
      })}
    </div>
  );
}
